import { isEmptyAudioSourceFeedback } from '@modules/feedback/components/AudioType/utils';
import { getHighlightAnimationKeyframes } from '@modules/feedback/components/AudioType/utils/getHighlightAnimationKeyframes';
import { useUpdateSummaryFlag } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import useFeedbackListStore from '@modules/feedback/store/useFeedbackListStore';
import { getAudioSummaryFlagData } from '@modules/feedback/utils';
import { useIsSnippetOrFeedbackSaved } from '@modules/savedFeedbackList/hooks';
import { PlayArrowRounded, TripOriginRounded } from '@mui/icons-material';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import ReportOffRoundedIcon from '@mui/icons-material/ReportOffRounded';
import { LoadingButton } from '@mui/lab';
import {
  alpha,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/system';
import {
  SummaryFlag,
  SummaryMessageUnit,
  SummaryMetadata,
} from 'generated/graphql';
import { useState } from 'react';
import { Feedback, FeedbackContentType } from 'v2/core/entities/feedback';
import { useGetUser } from 'v2/core/services/user';
import { shallow } from 'zustand/shallow';

interface KeywordOrReasonSummaryListItemProps {
  summary: SummaryMessageUnit;
  hoveredSummary: string | null;
  onClick: (metadata: SummaryMetadata | undefined) => void;
  setHoveredSummary: (summary: string | null) => void;
  shouldAnimate?: (metadata: SummaryMetadata | undefined) => boolean;
  renderActions?: (summary: SummaryMessageUnit) => JSX.Element | null;
  feedback: Feedback;
  labelID?: string;
  shouldRenderSavedSnippetsOnly?: boolean;
}

export const KeywordOrReasonSummaryListItem = ({
  summary,
  hoveredSummary,
  shouldAnimate,
  onClick,
  setHoveredSummary,
  renderActions,
  feedback,
  labelID = '',
  shouldRenderSavedSnippetsOnly = false,
}: KeywordOrReasonSummaryListItemProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const theme = useTheme();
  const snippetID = summary.text;
  const [isFlaggedSummaryHovered, setIsFlaggedSummaryHovered] = useState(false);
  const flaggedSummaryRecords = useFeedbackOnFeedbackStore(
    ({ flaggedSummaryRecords }) => flaggedSummaryRecords,
  );

  const {
    msgIdx,
    keywordSummaryHash,
    uniqueKeywordSummaryHash,
    initialFlag,
    isSummaryFlagged,
  } = getAudioSummaryFlagData({
    feedback,
    summary,
    labelID,
    flaggedSummaryRecords,
  });

  const getUnFlagSummaryStartIcon = () => {
    if (isFlaggedSummaryHovered) {
      return <ReportOffRoundedIcon />;
    }

    return <FlagRoundedIcon />;
  };

  const isSavedSnippet = useIsSnippetOrFeedbackSaved({
    feedbackID: feedback.id,
    snippetID,
    labelID,
  });

  const { isPlaying } = useFeedbackListStore(
    ({ feedbacks }) => ({
      isPlaying: feedbacks[feedback.id]?.isPlaying ?? false,
    }),
    shallow,
  );

  const { updateSummaryFlag, isLoading: isFlagLoading } = useUpdateSummaryFlag({
    feedbackID: feedback.id,
    keywordSummaryHash: uniqueKeywordSummaryHash,
    summaryFlagType: SummaryFlag.Incorrect,
    initialFlag,
    keywordSummaryText: summary.text,
  });

  const onUnFlagSummary = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    setIsFlaggedSummaryHovered(false);
    updateSummaryFlag({
      variables: {
        request: {
          userID,
          recordID: feedback.id,
          tenantID: tenantID,
          removeFlag: SummaryFlag.Incorrect,
          keywordSummaryLocation: {
            msgIdx,
            keywordSummaryHash,
          },
        },
      },
    });
  };

  const isAudioSourceEmpty = isEmptyAudioSourceFeedback(feedback);

  const shouldShowPlayIcon =
    feedback.content.type === FeedbackContentType.Audio && !isAudioSourceEmpty;

  const shouldHighlightSummary =
    (!isAudioSourceEmpty && shouldAnimate?.(summary.metadata)) ?? false;

  const shouldAnimateSummary = isPlaying && shouldHighlightSummary;

  const listItemIconColor = isSavedSnippet
    ? 'text.secondary'
    : shouldHighlightSummary
    ? 'primary.contrastText'
    : 'text.secondary';

  const listItemTextColor = shouldHighlightSummary
    ? isSavedSnippet
      ? 'text.secondary'
      : 'primary.contrastText'
    : 'text.secondary';

  if (!isSavedSnippet && shouldRenderSavedSnippetsOnly) {
    return null;
  }

  const highlightColor1 = theme.palette.warning.A100;
  const highlightColor2 = theme.palette.feedback.saved;

  return (
    <Paper
      component={Stack}
      spacing={0.5}
      elevation={summary.text === hoveredSummary ? 20 : 12}
      sx={{
        boxShadow: 'none',
        cursor: isAudioSourceEmpty ? 'default' : 'pointer',
        my: 1,
        animation: (theme) =>
          shouldAnimateSummary
            ? `${getHighlightAnimationKeyframes(
                theme.palette.primary.main,
                theme.palette.primary.dark,
              )} 0.7s linear infinite alternate`
            : 'none',
        backgroundColor: (theme) =>
          shouldHighlightSummary ? theme.palette.primary.main : undefined,
        ...(isSavedSnippet
          ? {
              animation: shouldAnimateSummary
                ? `${getHighlightAnimationKeyframes(
                    alpha(highlightColor1, 0.3),
                    highlightColor2,
                  )} 0.7s linear infinite alternate`
                : 'none',
              backgroundColor: shouldHighlightSummary
                ? alpha(highlightColor1, 0.3)
                : highlightColor2,
              border: shouldHighlightSummary
                ? `1px solid ${alpha(highlightColor1, 0.3)}`
                : undefined,
            }
          : {}),
      }}
      onMouseEnter={() => setHoveredSummary(summary.text)}
      onMouseLeave={() => setHoveredSummary(null)}
      onClick={() => {
        if (isAudioSourceEmpty) {
          return;
        }
        onClick(summary.metadata);
      }}
    >
      {isSummaryFlagged && (
        <Stack alignItems="start" pt={1} pl={2}>
          <LoadingButton
            loading={isFlagLoading}
            disableRipple
            variant="text"
            size="small"
            sx={{
              color: isFlaggedSummaryHovered
                ? listItemIconColor
                : 'warning.light',
              backgroundColor: 'primary.dark',
              minWidth: '300px',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
            startIcon={getUnFlagSummaryStartIcon()}
            onMouseOver={() => setIsFlaggedSummaryHovered(true)}
            onMouseOut={() => setIsFlaggedSummaryHovered(false)}
            onClick={onUnFlagSummary}
          >
            {isFlaggedSummaryHovered
              ? 'Remove Flag'
              : 'Flagged as “Incorrect Summary”'}
          </LoadingButton>
        </Stack>
      )}
      <Stack direction="row" alignItems="center" component={ListItem}>
        <ListItemIcon
          sx={{
            minWidth: '40px',
          }}
        >
          {shouldShowPlayIcon ? (
            <PlayArrowRounded
              sx={{
                color: listItemIconColor,
              }}
            />
          ) : (
            <TripOriginRounded
              sx={{
                width: 14,
                height: 14,
                color: listItemIconColor,
              }}
            />
          )}
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{
            variant: 'body2',
            color: listItemTextColor,
          }}
          primary={summary.text}
        />
        {renderActions?.(summary)}
      </Stack>
    </Paper>
  );
};
